import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text, useTheme, Card, Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FileText, Shield, Users, AlertCircle } from 'lucide-react-native';

/**
 * TermsAndConditionsScreen - Legal Documentation Display
 *
 * Production-ready implementation featuring:
 * - Comprehensive terms and conditions
 * - Privacy policy highlights
 * - User agreement sections
 * - Proper formatting and readability
 * - Last updated timestamp
 * - Accessibility support
 * - FIXED: Proper safe area handling
 *
 * @production-ready Staff-level legal documentation screen
 */
export function TermsAndConditionsScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: FileText,
      content:
        'By accessing and using CoffeePlexity ("the App"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use the App.',
    },
    {
      id: 'description',
      title: 'Description of Service',
      icon: Users,
      content:
        'CoffeePlexity is a coffee brewing platform that provides recipes, equipment reviews, creator content, and personalized coffee recommendations. We reserve the right to modify or discontinue the service at any time without notice.',
    },
    {
      id: 'user-accounts',
      title: 'User Accounts',
      icon: Shield,
      content:
        'You are responsible for maintaining the confidentiality of your account credentials. You agree to accept responsibility for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account.',
    },
    {
      id: 'content',
      title: 'User-Generated Content',
      icon: FileText,
      content:
        'By posting content on CoffeePlexity, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and distribute your content. You retain ownership of your content and are responsible for ensuring it does not violate any laws or third-party rights.',
    },
    {
      id: 'prohibited',
      title: 'Prohibited Activities',
      icon: AlertCircle,
      content:
        'You agree not to: (a) violate any laws or regulations; (b) infringe on intellectual property rights; (c) transmit harmful code or malware; (d) harass or harm other users; (e) attempt to gain unauthorized access to the App; (f) use the App for commercial purposes without permission.',
    },
    {
      id: 'privacy',
      title: 'Privacy & Data Protection',
      icon: Shield,
      content:
        'We are committed to protecting your privacy. Our Privacy Policy explains how we collect, use, and safeguard your personal information. We comply with GDPR, CCPA, and other applicable data protection regulations. You have the right to access, modify, or delete your personal data at any time.',
    },
    {
      id: 'creator',
      title: 'Creator Program',
      icon: Users,
      content:
        'Creators who participate in our Creator Program agree to additional terms regarding content quality, revenue sharing, and partnership obligations. Creator earnings are subject to our payment terms and applicable taxes.',
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      icon: FileText,
      content:
        'All content, trademarks, logos, and intellectual property on CoffeePlexity are owned by us or our licensors. You may not use our intellectual property without explicit written permission.',
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer of Warranties',
      icon: AlertCircle,
      content:
        'THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. YOUR USE OF THE APP IS AT YOUR OWN RISK.',
    },
    {
      id: 'limitation',
      title: 'Limitation of Liability',
      icon: Shield,
      content:
        'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF THE APP.',
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      icon: Shield,
      content:
        'You agree to indemnify and hold harmless CoffeePlexity and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the App or violation of these terms.',
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: AlertCircle,
      content:
        'We reserve the right to terminate or suspend your account at any time, with or without notice, for conduct that we believe violates these terms or is harmful to other users, us, or third parties.',
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      icon: FileText,
      content:
        'We reserve the right to modify these terms at any time. We will notify users of any material changes via email or in-app notification. Your continued use of the App after changes constitutes acceptance of the new terms.',
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      icon: Shield,
      content:
        'These terms shall be governed by and construed in accordance with the laws of the jurisdiction in which CoffeePlexity operates, without regard to conflict of law provisions.',
    },
    {
      id: 'contact',
      title: 'Contact Information',
      icon: Users,
      content:
        'For questions about these Terms and Conditions, please contact us at:\n\nEmail: legal@coffeeplexity.com',
    },
  ];

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.contentContainer}
    >
      {/* Header */}
      <View style={styles.header}>
        <Text
          variant='headlineMedium'
          style={[styles.title, { color: theme.colors.onSurface }]}
        >
          Terms & Conditions
        </Text>
        <Text
          variant='bodyMedium'
          style={{ color: theme.colors.onSurfaceVariant, marginTop: 8 }}
        >
          Last Updated: October 3, 2025
        </Text>
      </View>

      <Divider style={{ marginVertical: 16 }} />

      {/* Introduction */}
      <Card
        style={[
          styles.introCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='bodyMedium'
            style={{ color: theme.colors.onPrimaryContainer }}
          >
            Please read these Terms and Conditions carefully before using
            CoffeePlexity. These terms govern your access to and use of our
            services.
          </Text>
        </Card.Content>
      </Card>

      {/* Sections */}
      {sections.map((section, index) => {
        const IconComponent = section.icon;
        return (
          <View key={section.id} style={styles.section}>
            <View style={styles.sectionHeader}>
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: `${theme.colors.primary}15` },
                ]}
              >
                <IconComponent size={20} color={theme.colors.primary} />
              </View>
              <Text
                variant='titleMedium'
                style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
              >
                {index + 1}. {section.title}
              </Text>
            </View>
            <Text
              variant='bodyMedium'
              style={[
                styles.sectionContent,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              {section.content}
            </Text>
          </View>
        );
      })}

      {/* Footer */}
      <View style={styles.footer}>
        <Divider style={{ marginBottom: 16 }} />
        <Text
          variant='bodySmall'
          style={{
            color: theme.colors.onSurfaceVariant,
            textAlign: 'center',
          }}
        >
          © 2025 CoffeePlexity. All rights reserved.
        </Text>
        <Text
          variant='bodySmall'
          style={{
            color: theme.colors.onSurfaceVariant,
            textAlign: 'center',
            marginTop: 8,
          }}
        >
          By using CoffeePlexity, you agree to these Terms and Conditions and
          our Privacy Policy.
        </Text>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  header: {
    marginBottom: 8,
  },
  title: {
    fontWeight: '700',
  },
  introCard: {
    marginBottom: 24,
    borderRadius: 12,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  sectionTitle: {
    fontWeight: '600',
    flex: 1,
  },
  sectionContent: {
    lineHeight: 22,
    paddingLeft: 48,
  },
  footer: {
    marginTop: 16,
    paddingTop: 16,
  },
});
